const EVENT_DATE_LOCALE = "en-GB";

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dayMonth(date: Date): string {
  return date.toLocaleDateString(EVENT_DATE_LOCALE, {
    day: "numeric",
    month: "short",
    timeZone: "Europe/Helsinki",
  });
}

/** Builds the public event date label from the dates that control visibility. */
export function formatEventDateRange(startsAt?: string | null, endsAt?: string | null): string | null {
  const start = parseDate(startsAt);
  const end = parseDate(endsAt);

  if (!start && !end) return null;
  if (!start) return dayMonth(end as Date);
  if (!end) return dayMonth(start);

  const startParts = new Intl.DateTimeFormat(EVENT_DATE_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Helsinki",
  }).formatToParts(start);
  const endParts = new Intl.DateTimeFormat(EVENT_DATE_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Helsinki",
  }).formatToParts(end);
  const part = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPart["type"]) =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  const startDay = part(startParts, "day");
  const startMonth = part(startParts, "month");
  const startYear = part(startParts, "year");
  const endDay = part(endParts, "day");
  const endMonth = part(endParts, "month");
  const endYear = part(endParts, "year");

  if (startYear === endYear && startMonth === endMonth) return `${startDay}–${endDay} ${endMonth}`;
  if (startYear === endYear) return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
  return `${startDay} ${startMonth} ${startYear} – ${endDay} ${endMonth} ${endYear}`;
}

/**
 * Validates the canonical event dates that every displayed date label derives from.
 * Returns an error message when a label could not be produced consistently, else null.
 */
export function validateEventDates(
  startsAt?: string | null,
  endsAt?: string | null,
): string | null {
  const hasStart = !!startsAt;
  const hasEnd = !!endsAt;
  if (!hasStart && !hasEnd) return "Add an event start date so the public date label matches the event.";

  const start = parseDate(startsAt);
  const end = parseDate(endsAt);
  if (hasStart && !start) return "Start date is not a valid date.";
  if (hasEnd && !end) return "End date is not a valid date.";
  if (!start) return "Add an event start date — an end date alone produces a misleading label.";
  if (start && end && end.getTime() < start.getTime()) return "End date must be after the start date.";
  if (!formatEventDateRange(startsAt, endsAt)) return "These dates cannot produce a valid date label.";
  return null;
}
